import { useEffect, useState } from 'react';
import { RefreshCw, Database, CheckCircle2, XCircle, Percent, Eye } from 'lucide-react';
import { Card, CardHead } from '@/Components/ui/Card';
import { Select } from '@/Components/ui/Select';
import { Input } from '@/Components/ui/Input';
import { Checkbox } from '@/Components/ui/Checkbox';
import { IconButton } from '@/Components/ui/IconButton';
import { StatusBadge } from '@/Components/ui/Badge';
import { Button } from '@/Components/ui/Button';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { Field } from '@/Components/forms/Field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import sincronizacaoLogService, {
  type SincronizacaoLog,
  type SincronizacaoLogDetalhe,
  type SincronizacaoEstatisticas,
} from '@/services/sincronizacaoLog';

const STATUS_OPTIONS = [
  { value: 'INICIADO', label: 'Iniciado' },
  { value: 'SUCESSO', label: 'Sucesso' },
  { value: 'PARCIAL', label: 'Parcial' },
  { value: 'ERRO', label: 'Erro' },
];

const TIPO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'FULL', label: 'Completa' },
  { value: 'INCREMENTAL', label: 'Incremental' },
];

const ENTIDADE_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'EMPRESA', label: 'Empresas' },
  { value: 'PRODUTO', label: 'Produtos' },
  { value: 'USUARIO', label: 'Usuários' },
  { value: 'TODOS', label: 'Todas Entidades' },
];

const STATUS_TONE: Record<string, 'info' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  INICIADO: 'info',
  SUCESSO: 'success',
  PARCIAL: 'warning',
  ERRO: 'danger',
};

function hojeISO() {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;
}

export default function AdminSincronizacaoLogs() {
  const [logs, setLogs] = useState<SincronizacaoLog[]>([]);
  const [estatisticas, setEstatisticas] = useState<SincronizacaoEstatisticas | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  const [filtroStatus, setFiltroStatus] = useState<string[]>([]);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEntidade, setFiltroEntidade] = useState('');
  const [dataInicio, setDataInicio] = useState(hojeISO());
  const [dataFim, setDataFim] = useState(hojeISO());

  const [dialogDetalhes, setDialogDetalhes] = useState(false);
  const [logSelecionado, setLogSelecionado] = useState<SincronizacaoLogDetalhe | null>(null);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);

  const carregarLogs = async () => {
    setLoading(true);
    try {
      const data = await sincronizacaoLogService.listar({
        status: filtroStatus.length > 0 ? filtroStatus.join(',') : undefined,
        tipo: filtroTipo || undefined,
        entidade: filtroEntidade || undefined,
        data_inicio: dataInicio || undefined,
        data_fim: dataFim || undefined,
      });
      setLogs(data);
    } catch (error) {
      console.error('Erro ao carregar logs de sincronização', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarEstatisticas = async () => {
    setLoadingStats(true);
    try {
      const data = await sincronizacaoLogService.estatisticas();
      setEstatisticas(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas', error);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    carregarLogs();
    carregarEstatisticas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleStatus = (status: string) => {
    setFiltroStatus((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]));
  };

  const limparFiltros = () => {
    setFiltroStatus([]);
    setFiltroTipo('');
    setFiltroEntidade('');
    setDataInicio('');
    setDataFim('');
  };

  const verDetalhes = async (log: SincronizacaoLog) => {
    setDialogDetalhes(true);
    setLoadingDetalhes(true);
    try {
      const detalhe = await sincronizacaoLogService.buscarPorId(log.id);
      setLogSelecionado(detalhe);
    } catch (error) {
      console.error('Erro ao carregar detalhes do log', error);
    } finally {
      setLoadingDetalhes(false);
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<Database size={21} strokeWidth={2} />}
        title="Logs de Sincronização ERP"
        description="Histórico das sincronizações executadas entre o sistema e o ERP legado."
        actions={
          <Button
            type="button"
            variant="ghost"
            onClick={() => { carregarLogs(); carregarEstatisticas(); }}
            loading={loading}
            loadingText="Atualizando..."
          >
            <RefreshCw size={16} strokeWidth={2.2} />
            Atualizar
          </Button>
        }
      />

      <div className="nx-kpi-grid">
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Sincronizações Hoje</span>
            <div className="nx-kpi-icon">
              <RefreshCw size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value">{loadingStats ? '...' : estatisticas?.sincronizacoes_hoje ?? 0}</strong>
          </div>
        </article>
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Sucesso Hoje</span>
            <div className="nx-kpi-icon">
              <CheckCircle2 size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value" style={{ color: 'var(--nx-ok)' }}>
              {loadingStats ? '...' : estatisticas?.sucesso_hoje ?? 0}
            </strong>
          </div>
        </article>
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Erros Hoje</span>
            <div className="nx-kpi-icon">
              <XCircle size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value" style={{ color: 'var(--nx-danger)' }}>
              {loadingStats ? '...' : estatisticas?.erros_hoje ?? 0}
            </strong>
          </div>
        </article>
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Taxa de Sucesso</span>
            <div className="nx-kpi-icon">
              <Percent size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value">{loadingStats ? '...' : `${estatisticas?.taxa_sucesso ?? 0}%`}</strong>
          </div>
        </article>
      </div>

      <Card className="nx-page-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <Select filter value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                {TIPO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
              <Select filter value={filtroEntidade} onChange={(e) => setFiltroEntidade(e.target.value)}>
                {ENTIDADE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            </div>
          }
        />

        <div className="nx-form-grid" style={{ padding: '0 1.25rem 1.25rem' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <span style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--nx-muted)' }}>
              Status
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              {STATUS_OPTIONS.map((s) => (
                <label key={s.value} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                  <Checkbox checked={filtroStatus.includes(s.value)} onChange={() => toggleStatus(s.value)} />
                  {s.label}
                </label>
              ))}
            </div>
          </div>
          <Field label="Data início" htmlFor="sync-log-data-inicio">
            <Input
              id="sync-log-data-inicio"
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
            />
          </Field>
          <Field label="Data fim" htmlFor="sync-log-data-fim">
            <Input
              id="sync-log-data-fim"
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
            />
          </Field>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button type="button" variant="ghost" onClick={limparFiltros}>Limpar Filtros</Button>
            <Button type="button" onClick={carregarLogs}>Aplicar Filtros</Button>
          </div>
        </div>

        {!loading && logs.length === 0 ? (
          <EmptyState message="Nenhum log encontrado." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  <th>Tipo</th>
                  <th>Entidade</th>
                  <th>Status</th>
                  <th>Duração</th>
                  <th>Processados</th>
                  <th>Erros</th>
                  <th>Mensagem</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id}>
                      <td>{log.iniciado_em ? new Date(log.iniciado_em).toLocaleString('pt-BR') : '-'}</td>
                      <td>{log.tipo}</td>
                      <td>{log.entidade}</td>
                      <td>
                        <StatusBadge label={log.status} tone={STATUS_TONE[log.status] || 'neutral'} />
                      </td>
                      <td>{log.duracao_formatada}</td>
                      <td>{log.total_processados}</td>
                      <td>{log.total_erros}</td>
                      <td
                        title={log.mensagem || ''}
                        style={{ maxWidth: '18rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {log.mensagem || '-'}
                      </td>
                      <td className="nx-col-acoes">
                        <IconButton label="Ver detalhes" onClick={() => verDetalhes(log)}>
                          <Eye size={15} strokeWidth={1.9} aria-hidden />
                        </IconButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={dialogDetalhes} onOpenChange={setDialogDetalhes}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detalhes da Sincronização</DialogTitle>
            <DialogDescription>Informações completas do registro de sincronização selecionado.</DialogDescription>
          </DialogHeader>

          {loadingDetalhes ? (
            <p style={{ padding: '1.5rem 0', textAlign: 'center', color: 'var(--nx-muted)' }}>Carregando...</p>
          ) : logSelecionado ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', fontSize: '0.9rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div><span style={{ color: 'var(--nx-muted)' }}>Tipo:</span> {logSelecionado.tipo}</div>
                <div><span style={{ color: 'var(--nx-muted)' }}>Entidade:</span> {logSelecionado.entidade}</div>
                <div><span style={{ color: 'var(--nx-muted)' }}>Status:</span> {logSelecionado.status}</div>
                <div><span style={{ color: 'var(--nx-muted)' }}>Duração:</span> {logSelecionado.duracao_formatada}</div>
              </div>
              {logSelecionado.mensagem && (
                <div style={{ borderRadius: 'var(--nx-radius-sm)', background: 'var(--nx-accent-soft)', padding: '0.75rem', color: 'var(--nx-muted)' }}>
                  {logSelecionado.mensagem}
                </div>
              )}
              <div>
                <h4 style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Empresas</h4>
                <p style={{ color: 'var(--nx-muted)' }}>
                  Criadas: {logSelecionado.resumo_empresas.criadas} · Atualizadas: {logSelecionado.resumo_empresas.atualizadas} · Erros: {logSelecionado.resumo_empresas.erros}
                </p>
              </div>
              <div>
                <h4 style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Produtos</h4>
                <p style={{ color: 'var(--nx-muted)' }}>
                  Criados: {logSelecionado.resumo_produtos.criados} · Atualizados: {logSelecionado.resumo_produtos.atualizados} · Erros: {logSelecionado.resumo_produtos.erros}
                </p>
              </div>
              <div>
                <h4 style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Usuários</h4>
                <p style={{ color: 'var(--nx-muted)' }}>
                  Criados: {logSelecionado.resumo_usuarios.criados} · Atualizados: {logSelecionado.resumo_usuarios.atualizados} · Erros: {logSelecionado.resumo_usuarios.erros}
                </p>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
